/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/require-default-props */
import React, { ChangeEvent, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  DatePicker,
  InputAutosuggest,
  Layout,
  Select as SelectBR,
  Spinner,
} from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { t, TFunction } from 'i18next';
import { get } from 'lodash';
import moment from 'moment';
import { FormField } from '../../../components/FormField/FormField';
import {
  charsReplacementMap1,
  getMobilePhoneLengthMaskForCountry,
  MaskedTextFormField,
  masks,
} from '../../../components/FormFields/MaskedTextFormField';
import { NumericFormField } from '../../../components/FormFields/NumericFormField';
import { SelectFormField } from '../../../components/FormFields/SelectFormField';
import { StaticTextFormField } from '../../../components/FormFields/StaticTextFormField';
import { TextFormField } from '../../../components/FormFields/TextFormField';
import {
  entityLegalFormOptions,
  genderOptions,
  housingSituationOptions,
  kindOfCompany,
  maritalStatusOptions,
  professionOptions,
  salutationOptions,
  soleTraderProfessionOptions,
  telephoneCountryCodesOptions,
  yesOrNot,
} from '../../../resources/base/models';
import { countryCodesOptions, translateCountryCodeToCountry } from '../../../resources/base/models/country';
import { CustomerDetailsSection } from '../../../resources/base/models/enums';
import { CitiesAndZipCodesSuggestion } from '../../../services/api/address/localitiesAndZipCodes';
import { validateIBAN } from '../../../services/api/iban/validIBAN';
import { normalizeDate } from '../../../services/common/format';
import { getIsSoleTrader } from '../../../services/common/miscUtils';
import { getMaxBirthDate } from '../../../services/common/validation';
import { staticSelector } from '../../../services/redux';
import { customerSelectors } from '../../../services/redux/features/customer.slice';
import {
  AddressDataFormType,
  GeneralInfoDataFormType,
  SignatoryInfoDataFormType,
} from '../Step4/businessCustomerDetailsForms/initialValues';
import { formatEmail } from './emailFormatter';
import { formatCityNameLettersAndSpaceOnly } from './formatCityString';
import { setBicValueFromIban } from './validateIbanUtil';

const nonBreakingSpace = '\xa0';

export interface Props {
  t: TFunction;
  setFieldValue?: any;
  handleChange?(e: ChangeEvent<any>): void;
  values?: any;
  cocontractor?: boolean;
  setCocontractor?: any;
  checkOut?: boolean;
  formData?: any;
  customerHasCompany?: boolean;
  setCustomerHasCompany?: any;
  index?: number;
  toggle?: boolean;
  setToggle?: any;
  contractAssigner?: boolean;
  setContractAssigner?: any;
  setLegalEntity?: any;
  label?: any;
  infoText?: string;
  errors?: any;
  name?: any;
  checked?: boolean;
  ceoBirthCountry?: any;
  setCeoFromFrance?: any;
  legalEntityValue?: any;
  isIndividualEntrepreneur?: boolean;
  customerType?: any;
  tlsCheckStatus?: any;
  currentPage?: any;
  ceoFromFrance?: any;
  professionCocontractor?: any;
  profession?: any;
  financialProduct?: any;
  isLoading?: any;
  setIsLoading?: any;
  required?: boolean;
  chosenProductType?: any;
  readOnly?: boolean;
  placeholder?: string;
  generalSubmittedBC?: boolean;
  setGeneralSubmittedBC?: any;
  addressSubmittedBC?: any;
  setAddressSubmittedBC?: any;
  additionalSubmittedBC?: boolean;
  setAdditionalSubmittedBC?: any;
  submittedSections?: CustomerDetailsSection[];
  setSubmittedSections?: any;
  hybridJourney?: boolean;
  companySubmittedBC?: boolean;
  setCompanySubmittedBC?: any;
  personalSubmittedBC?: boolean;
  setPersonalSubmittedBC?: any;
  onClick?: () => void;
  isDisabled?: boolean;
  isSectionLoading?: boolean;
  isSoleTrader?: boolean;
}

export const SIRENWebserviceCall: FunctionComponent<Props> = () => {
  return (
    <MaskedTextFormField
      fieldName="sirenWebserviceCall"
      labelTranslationKey="step2:SIRENWebserviceCall:label"
      mask={masks.sirenMask}
    />
  );
};

export const SIRENWebserviceCallNew: FunctionComponent<Props> = ({ index }) => {
  const fieldName = `companySIRENnumber_${index}`;
  return (
    <MaskedTextFormField
      fieldName={fieldName}
      labelTranslationKey="step4:companySirenNumber:label"
      mask={masks.sirenMask}
    />
  );
};

export const SIRENWebserviceCallButton: FunctionComponent<Props> = ({ t, onClick, isDisabled }) => (
  <FormField
    name="sirenWebserviceCallButton"
    type="button"
    labelText={nonBreakingSpace} // An empty string or simple space would cause a broken layout
    render={() => (
      <Button name="sirenWebserviceCallButton" type="button" onClick={onClick} disabled={isDisabled} full>
        {t('step4:SIRENWebserviceCallInput:label')}
      </Button>
    )}
  />
);

export const KindOfCompanyNew: FunctionComponent<Props> = ({ t, index, setFieldValue }) => {
  return (
    <FormField
      required
      type="select"
      name={`businessCustomerHasCompany_${index}`}
      labelText={t('step4:customerHasCompany:kindOfCompany:label')}
      render={(fieldProps) => (
        <SelectBR
          {...fieldProps}
          onChange={(event) => {
            setFieldValue(`businessCustomerHasCompany_${index}`, event?.target?.value);
          }}
        >
          {kindOfCompany?.map(({ label, value }) => (
            <SelectBR.Item key={`${label}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

interface LegalEntityProps extends Props {
  filterSoleTrader?: boolean;
}

export const LegalEntity: FunctionComponent<LegalEntityProps> = ({ isDisabled }) => {
  const milesCustomerId = staticSelector(customerSelectors.selectCustomerId);
  const allowSoleTrader = milesCustomerId && getIsSoleTrader(staticSelector(customerSelectors.selectLegalEntity));
  const entityLegalFormOptionsFiltered = allowSoleTrader
    ? entityLegalFormOptions.filter(({ value }) => value !== '' && getIsSoleTrader(value))
    : entityLegalFormOptions;

  return (
    <FormField
      required
      type="select"
      name="legalEntity"
      labelText={t('step2:legalEntity:label')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps} disabled={isDisabled}>
          {entityLegalFormOptionsFiltered.map(({ label, value }) => (
            <SelectBR.Item key={value} value={value}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const CompanyName: FunctionComponent<Props> = ({ isDisabled }) => (
  <MaskedTextFormField
    mask={masks.companyNameMask}
    fieldName="companyName"
    labelTranslationKey="step2:companyName:label"
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
    isDisabled={isDisabled}
  />
);

export const APECode: FunctionComponent<Props> = ({ isDisabled }) => {
  return (
    <MaskedTextFormField
      fieldName="apecode"
      labelTranslationKey="step2:APECode:label"
      mask={masks.apeCodeMask}
      isDisabled={isDisabled}
    />
  );
};

export const TelephonePhone: FunctionComponent<Props> = () => {
  return (
    <MaskedTextFormField
      fieldName="telephonePhone"
      labelTranslationKey="step2:telephonePhone:label"
      mask={masks.phonePrefixContactMask}
      placeholder="+33 ##########"
      optional
    />
  );
};

export const CountryCodeMobilePhone: FunctionComponent<Props> = ({ t, isDisabled }) => (
  <FormField
    className="u-text-left"
    name="mobilePhoneCountryCode"
    type="select"
    labelText={t('sf-sac:continueLater:form:customerPhoneCountryCode')}
    required
    render={(fieldProps) => (
      <SelectBR {...fieldProps} disabled={isDisabled}>
        {telephoneCountryCodesOptions?.map(({ label, value }) => (
          <SelectBR.Item key={`${label}`} value={`${value}`}>
            {label}
          </SelectBR.Item>
        ))}
      </SelectBR>
    )}
  />
);

export const MobilePhone: FunctionComponent<Props> = ({ isDisabled, values, required = true }) => {
  const mask = getMobilePhoneLengthMaskForCountry(values.mobilePhoneCountryCode);
  const isFrenchMobilePhone = values.mobilePhoneCountryCode === '+33';

  return (
    <MaskedTextFormField
      fieldName="mobilePhone"
      labelTranslationKey="step2:mobilePhone:label"
      mask={mask}
      placeholder="##########"
      isDisabled={isDisabled}
      optional={!required}
      infoTextTranslationKey={isFrenchMobilePhone ? 'step2:mobilePhone:infoText' : undefined}
    />
  );
};

export const Email: FunctionComponent<Props> = ({ isDisabled }) => {
  const { setFieldValue } = useFormikContext<SignatoryInfoDataFormType | GeneralInfoDataFormType>();
  const { t } = useTranslation();
  return (
    <TextFormField
      fieldName="email"
      labelTranslationKey={t('step4:email:label')}
      isDisabled={isDisabled}
      onChange={(event) => {
        setFieldValue('email', formatEmail(event.target.value));
      }}
    />
  );
};

export const PrivateUserCheck: FunctionComponent<Props> = ({ t, setFieldValue, values }) => (
  <FormField
    name="privateUserCheck"
    type="input"
    // Whitespace character in title to have checkbox aligned with proper spacing
    labelText="&nbsp;"
    render={() => (
      <Checkbox
        name="privateUserCheck"
        onChange={(e) => {
          setFieldValue('privateUserCheck', e.target.checked);
        }}
        checked={values?.privateUserCheck}
      >
        {t('step2:texts:carForPrivateUser')}
      </Checkbox>
    )}
  />
);

export const PreviousContractNumber: FunctionComponent<Props> = () => (
  <TextFormField fieldName="previousContractNumber" labelTranslationKey="step2:previousContractNumber:label" />
);

export const CarToBeReturned: FunctionComponent<Props> = ({ t }) => {
  return (
    <FormField
      required
      type="select"
      name="carToBeReturned"
      labelText={t('step2:heading:carToBeReturned')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps}>
          {yesOrNot?.map(({ label, value }) => (
            <SelectBR.Item key={`${label}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const CompanyCreationDate: FunctionComponent<Props & { infoText: string }> = ({
  label,
  setFieldValue,
  values,
  placeholder,
  infoText,
  isDisabled,
}) => {
  const value = get(values, 'companyCreationDate', '');

  return (
    <FormField
      name="companyCreationDate"
      type="other"
      labelText={label}
      infoText={infoText}
      required
      render={(fieldProps) => (
        <DatePicker
          {...fieldProps}
          name="companyCreationDate"
          datePickerClassName="c-input__disabled-validation"
          maxDate={moment().toDate()}
          dateFormat="d/m/Y"
          locale="fr"
          placeholder={placeholder ?? 'd/m/Y'}
          mode="single"
          value={value}
          onChange={(e) => setFieldValue('companyCreationDate', e)}
          onBlur={(e) => setFieldValue('companyCreationDate', e)}
          disabled={isDisabled}
        />
      )}
    />
  );
};

export const OpeningDate: FunctionComponent<Props> = ({ label, setFieldValue, values }) => {
  const { setFieldValue: setFieldValueInternal } = useFormikContext<any>();
  return (
    <FormField
      name="openingDate"
      type="other"
      labelText={label}
      required
      render={(fieldProps) => (
        <DatePicker
          {...fieldProps}
          name="openingDate"
          datePickerClassName="c-input__disabled-validation"
          maxDate={moment().toDate()}
          dateFormat="d/m/Y"
          locale="fr"
          mode="single"
          value={get(values, 'openingDate', '')}
          onChange={(e) => {
            setFieldValueInternal('openingDate', normalizeDate(e));
            setFieldValue('openingDate', e);
          }}
          onBlur={(e) => {
            setFieldValueInternal('openingDate', normalizeDate(e));
            setFieldValue('openingDate', e);
          }}
        />
      )}
    />
  );
};

export const ZipCode: FunctionComponent<Props & { onManualAddressInput(): void }> = ({ onManualAddressInput }) => {
  return (
    <MaskedTextFormField
      fieldName="zipCode"
      labelTranslationKey="step4:zipCode:label"
      mask={masks.zipCodeMask}
      onAccept={() => onManualAddressInput()}
    />
  );
};

export const City: FunctionComponent<Props & { onManualAddressInput(): void }> = ({
  handleChange,
  onManualAddressInput,
}) => (
  <TextFormField
    fieldName="city"
    labelTranslationKey="step4:city:label"
    onChange={(e) => {
      handleChange!(e as ChangeEvent<any>);
      onManualAddressInput();
    }}
  />
);

export const Country: FunctionComponent = () => {
  const { values } = useFormikContext<AddressDataFormType>();
  return (
    <StaticTextFormField
      fieldName="country"
      labelTranslationKey="step4:country:label"
      displayValue={translateCountryCodeToCountry(values.country)}
    />
  );
};

export const AddressLine1: FunctionComponent<Props & { onManualAddressInput(): void }> = ({
  handleChange,
  onManualAddressInput,
}) => (
  <TextFormField
    fieldName="addressLine1"
    labelTranslationKey="step4:addressLine1:label"
    onChange={(e) => {
      handleChange!(e as ChangeEvent<any>);
      onManualAddressInput();
    }}
  />
);

export const AddressLine2: FunctionComponent<Props & { onManualAddressInput(): void }> = () => (
  <TextFormField fieldName="optionalLine" labelTranslationKey="step4:optionalLine:label" optional />
);

export const Gender: FunctionComponent<Props> = ({ isDisabled }) => {
  return (
    <FormField
      required
      type="select"
      name="gender"
      labelText={t('step4:gender:label')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps} value={fieldProps.value ?? ''} disabled={isDisabled}>
          {genderOptions?.map(({ label, value }) => (
            <SelectBR.Item key={`${label}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const Salutation: FunctionComponent<Props> = ({ isDisabled }) => {
  return (
    <FormField
      type="select"
      name="salutation"
      labelText={t('step4:salutation:label')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps} disabled={isDisabled}>
          {salutationOptions?.map(({ label, value }) => (
            <SelectBR.Item key={`${label}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const LastName: FunctionComponent<Props> = ({ isIndividualEntrepreneur }) => (
  <MaskedTextFormField
    mask={masks.nameMask}
    fieldName={isIndividualEntrepreneur ? 'lastName' : 'assignerLastName'}
    labelTranslationKey="step2:lastName:label"
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
  />
);

export const BirthName: FunctionComponent<Props> = () => (
  <TextFormField fieldName="birthName" labelTranslationKey="step2:birthName:label" />
);

export const DateOfBirth: FunctionComponent<Props> = ({ label, setFieldValue, values, infoText }) => (
  <FormField
    name="dateOfBirth"
    type="other"
    labelText={label}
    required
    infoText={infoText}
    render={(fieldProps) => (
      <DatePicker
        {...fieldProps}
        name="dateOfBirth"
        datePickerClassName="c-input__disabled-validation"
        maxDate={moment().subtract(18, 'years').toDate()}
        dateFormat="d/m/Y"
        locale="fr"
        placeholder="j/m/a"
        mode="single"
        value={get(values, 'dateOfBirth', '')}
        onChange={(e) => setFieldValue('dateOfBirth', e)}
        onBlur={(e) => setFieldValue('dateOfBirth', e)}
      />
    )}
  />
);

export const ProfessionEntrepreneur: FunctionComponent<Props> = ({ t, isSoleTrader }) => {
  const dropdownEntries = isSoleTrader ? soleTraderProfessionOptions : professionOptions;
  return (
    <FormField
      required
      type="select"
      name="professionEntrepreneur"
      labelText={t('step4:socioProfessional:label')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps}>
          {dropdownEntries.map(({ label, value }) => (
            <SelectBR.Item key={`${value}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const MaritalStatus: FunctionComponent<Props> = ({ t }) => {
  return (
    <FormField
      required
      type="select"
      name="maritalStatus"
      labelText={t('step4:maritalStatus:label')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps}>
          {maritalStatusOptions?.map(({ label, value }) => (
            <SelectBR.Item key={`${label}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const NumberOfDependentChildren: FunctionComponent<Props> = () => (
  <NumericFormField
    type="numeric"
    fieldName="numberOfDependentChildren"
    labelTranslationKey="step4:numberOfDependentChildren:label"
    maximumFractionDigits={0}
    allowEmpty
  />
);

export const NetIncomeAmount: FunctionComponent<Props> = () => (
  <NumericFormField
    type="numeric"
    fieldName="netIncomeAmount"
    labelTranslationKey="step4:netIncomeAmount:label"
    maximumFractionDigits={2}
  />
);

export const ResidenceStatus: FunctionComponent<Props> = ({ t }) => {
  return (
    <FormField
      required
      type="select"
      name="residenceStatus"
      labelText={t('step4:residenceStatus:label')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps}>
          {housingSituationOptions?.map(({ label, value }) => (
            <SelectBR.Item key={`${label}`} value={`${value}`}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const OtherIncome: FunctionComponent<Props> = () => (
  <NumericFormField
    type="numeric"
    fieldName="otherIncome"
    labelTranslationKey="step4:otherIncomeAmount:label"
    maximumFractionDigits={2}
  />
);

export const MonthlyPropertyCharge: FunctionComponent<Props> = () => (
  <NumericFormField
    type="numeric"
    fieldName="monthlyPropertyCharge"
    labelTranslationKey="step4:monthlyPropertyCharge:label"
    maximumFractionDigits={2}
  />
);

export const OtherMonthlyCosts: FunctionComponent<Props> = () => (
  <NumericFormField
    type="numeric"
    fieldName="otherMonthlyCosts"
    labelTranslationKey="step4:otherMonthlyCosts:label"
    maximumFractionDigits={2}
  />
);

export const CeoFirstName: FunctionComponent<Props> = ({ isDisabled }) => (
  <MaskedTextFormField
    mask={masks.nameMask}
    fieldName="ceoFirstName"
    labelTranslationKey="step4:ceoFirstName:label"
    isDisabled={isDisabled}
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
  />
);

export const CeoLastName: FunctionComponent<Props> = ({ isDisabled }) => (
  <MaskedTextFormField
    mask={masks.nameMask}
    fieldName="ceoLastName"
    labelTranslationKey="step4:ceoLastName:label"
    isDisabled={isDisabled}
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
  />
);

export const CeoDateOfBirth: FunctionComponent<Props> = ({ label, setFieldValue, values, isDisabled }) => {
  const { setFieldValue: setFieldValueInternal } = useFormikContext<any>();
  return (
    <FormField
      name="ceoDateOfBirth"
      type="other"
      labelText={label}
      required
      render={(fieldProps) => (
        <DatePicker
          {...fieldProps}
          name="ceoDateOfBirth"
          datePickerClassName="c-input__disabled-validation"
          maxDate={getMaxBirthDate()}
          dateFormat="d/m/Y"
          locale="fr"
          mode="single"
          disabled={isDisabled}
          flatpickrOptions={{
            onClose: (e: any) => {
              const date = normalizeDate(e);
              try {
                if (date === 'Invalid date') {
                  throw new Error('');
                }
                setFieldValue('ceoDateOfBirth', normalizeDate(e));
                setFieldValueInternal('ceoDateOfBirth', normalizeDate(e));
              } catch (error) {
                setFieldValue('ceoDateOfBirth', '');
                setFieldValueInternal('ceoDateOfBirth', '');
              }
            },
          }}
          value={get(values, 'ceoDateOfBirth', '')}
          onChange={(e) => {
            setFieldValueInternal('ceoDateOfBirth', normalizeDate(e));
            setFieldValue('ceoDateOfBirth', e);
          }}
          onBlur={(e) => {
            setFieldValueInternal('ceoDateOfBirth', normalizeDate(e));
            setFieldValue('ceoDateOfBirth', e);
          }}
        />
      )}
    />
  );
};
export const CeoBirthName: FunctionComponent<Props> = ({ isDisabled }) => (
  <MaskedTextFormField
    mask={masks.nameMask}
    fieldName="ceoBirthName"
    labelTranslationKey="step4:ceoBirthName:label"
    isDisabled={isDisabled}
    overwrite
    onAcceptCharsReplacementMap={charsReplacementMap1}
  />
);

export const CeoBirthCountry: FunctionComponent<Props> = ({ isDisabled }) => {
  const { setFieldValue } = useFormikContext<any>();
  return (
    <SelectFormField
      fieldName="ceoBirthCountry"
      labelTranslationKey="step4:ceoBirthCountry:label"
      dropDownOptionsList={countryCodesOptions}
      isDisabled={isDisabled}
      onChange={(selectedValue) => {
        const birthCountry = selectedValue.target.value;
        const postalCodeOfBirthCountry = countryCodesOptions.find(
          (country) => country.value === birthCountry
        )?.postalCode;
        setFieldValue('ceoBirthPlaceZip', postalCodeOfBirthCountry);
        setFieldValue('ceoBirthCountry', birthCountry);
      }}
    />
  );
};

export const CeoNationality: FunctionComponent<Props> = ({ t, isDisabled }) => {
  return (
    <FormField
      required
      type="select"
      name="ceoNationality"
      labelText={t('step4:ceoNationality:label')}
      render={(fieldProps) => (
        <SelectBR {...fieldProps} disabled={isDisabled}>
          {countryCodesOptions?.map(({ label, id, value }) => (
            <SelectBR.Item key={`${id}-${fieldProps.name}`} value={value}>
              {label}
            </SelectBR.Item>
          ))}
        </SelectBR>
      )}
    />
  );
};

export const CeoBirthPlaceZip: FunctionComponent<
  Props & {
    suggestions?: CitiesAndZipCodesSuggestion[];
    onSelectSuggestion?: (suggestion: string) => void;
    onChange?: (text: string) => void;
  }
> = ({ required, isDisabled, suggestions, isLoading, onChange, onSelectSuggestion }) => {
  const fieldName = 'ceoBirthPlaceZip';
  const { values } = useFormikContext<any>();
  return (
    <FormField
      name={fieldName}
      type="text"
      labelText={t('step4:ceoBirthPlaceZip:label')}
      disabled={isDisabled}
      required={required}
      render={() => (
        <InputAutosuggest
          value={values.ceoBirthPlaceZip}
          name={fieldName}
          list={suggestions?.map(({ city, zipCode }) => `${zipCode} ${city}`)}
          awesompleteProps={{
            filter: () => true,
            maxItems: 20,
          }}
          addonSpinner={isLoading ? <Spinner /> : null}
          disabled={isDisabled}
          openOnFocus
          onChange={(e) => {
            onChange?.(e.target.value.replace(/\D+/g, ''));
          }}
          onSelect={(e: any) => {
            onSelectSuggestion?.(e.text.value);
          }}
        />
      )}
    />
  );
};

export const CeoBirthPlaceCity: FunctionComponent<
  Props & {
    suggestions?: CitiesAndZipCodesSuggestion[];
    onSelectSuggestion?: (suggestion: string) => void;
    onChange?: (text: string) => void;
  }
> = ({ required, isDisabled, isLoading, suggestions, onChange, onSelectSuggestion }) => {
  const { values } = useFormikContext<any>();
  return (
    <FormField
      name="ceoBirthPlaceCity"
      type="text"
      labelText={t('step4:ceoBirthPlaceCity:label')}
      disabled={isDisabled}
      required={required}
      render={() => (
        <InputAutosuggest
          value={values.ceoBirthPlaceCity}
          name="ceoBirthPlaceCity"
          list={suggestions?.map(({ city, zipCode }) => `${city} ${zipCode}`)}
          awesompleteProps={{
            filter: () => true,
            maxItems: 20,
          }}
          addonSpinner={isLoading ? <Spinner /> : null}
          disabled={isDisabled}
          openOnFocus
          onChange={(e) => {
            const newTransformedValue = formatCityNameLettersAndSpaceOnly(e.target.value);
            onChange?.(newTransformedValue || '');
          }}
          onSelect={(e: any) => {
            onSelectSuggestion?.(e.text.value);
          }}
        />
      )}
    />
  );
};

export const BankAccountOwner: FunctionComponent<Props> = () => (
  <TextFormField fieldName="bankAccountOwner" labelTranslationKey="step4:bankAccountOwner:label" />
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Iban: FunctionComponent<Props> = ({ setFieldValue, setIsLoading }) => {
  const handleOnBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    await setBicValueFromIban(e.target.value, setFieldValue, validateIBAN);
  };

  return (
    <MaskedTextFormField
      mask={masks.ibanMask}
      fieldName="iban"
      labelTranslationKey="step4:iban:label"
      onBlurCapture={handleOnBlur}
      overwrite
      removeWhitespace
    />
  );
};

export const Bic: FunctionComponent<Props> = ({ isLoading }) => {
  return (
    <TextFormField
      fieldName="bic"
      labelTranslationKey="step4:bic:label"
      isDisabled={isLoading}
      addonSpinner={isLoading && <Spinner center small />}
    />
  );
};

export const FirstName: FunctionComponent<Props> = ({ isIndividualEntrepreneur }) => {
  return (
    <MaskedTextFormField
      mask={masks.nameMask}
      fieldName={isIndividualEntrepreneur ? 'firstName' : 'assignerFirstName'}
      labelTranslationKey="step4:firstName:label"
      overwrite
      onAcceptCharsReplacementMap={charsReplacementMap1}
    />
  );
};

export const MobilePhoneNonCio: FunctionComponent<Props> = () => {
  return (
    <MaskedTextFormField
      fieldName="assignerMobilePhone"
      labelTranslationKey="step2:mobilePhone:label"
      mask={masks.phonePrefixContactMask}
      placeholder="+33 ##########"
    />
  );
};

export const EmailNonCio: FunctionComponent<Props> = () => (
  <TextFormField fieldName="assignerEmail" labelTranslationKey="step4:email:label" />
);

export const ContractAssigner: FunctionComponent<Props> = ({ setContractAssigner }) => {
  const [toggle, setToggle] = useState(true);
  if (!toggle && setContractAssigner) {
    setContractAssigner(true);
  } else {
    setContractAssigner(false);
  }
  return (
    <>
      <br />
      <Checkbox onClick={() => setToggle(!toggle)}>{t('step4:contractAssigner:label')}</Checkbox>
      {!toggle && (
        <>
          <Layout>
            <Layout.Item default="1/1" s="1/1" />
            <Layout.Item default="1/2" s="1/1">
              <Gender t={t} />
            </Layout.Item>
            <Layout.Item default="1/1" s="1/1" />
            <Layout.Item default="1/2" s="1/1">
              <FirstName t={t} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <LastName t={t} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <MobilePhoneNonCio t={t} />
            </Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <EmailNonCio t={t} />
            </Layout.Item>
          </Layout>
        </>
      )}
    </>
  );
};
